/**
 * The privacy policy and account-deletion text — the one copy of it.
 *
 * Two readers: scripts/generate-legal-pages.mjs writes static HTML to
 * public/legal/ at build time (what nginx serves at /privacy and
 * /account-deletion, readable by store crawlers that run no JavaScript), and
 * pages/LegalPages.js shows the same HTML inside the app. Plain ESM with no
 * imports so Node can load it directly.
 *
 * Every statement describes something the system actually does. When a
 * behaviour changes — a new processor, a new kind of data, a retention rule —
 * change this text with it, and move EFFECTIVE.
 */

export const EFFECTIVE = '1 October 2026';

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function legalDocuments({ entity = 'Aqua ERP', contact = '' } = {}) {
  const who = esc(entity || 'Aqua ERP');
  const reach = contact
    ? `<a href="mailto:${esc(contact)}">${esc(contact)}</a>`
    : 'your company’s Aqua ERP administrator';

  const privacy = `
<p>Aqua ERP is business software for seafood processing and export companies. It is used by staff of those companies, on the web and through the Aqua ERP app for Android and iOS. This policy explains what information the service holds, why, and the choices you have.</p>

<h2>Who is responsible for your data</h2>
<p>Each company using Aqua ERP has its own separate workspace and database. The company decides what is recorded in its workspace and who can use it, and is responsible for that information. ${who} runs the service and processes the information on the company’s behalf, only to provide the service to it. Questions about records your company keeps should first go to your company; you can also contact us at ${reach}.</p>

<h2>What we hold</h2>
<ul>
<li><b>Your account</b> — name, email address, phone number (if added), role and the parts of the system you may use, created by your company’s administrator. Passwords are stored only as a one-way hash (bcrypt).</li>
<li><b>Business records</b> your company enters: purchases, processing, stock, quality, sales, accounts and payroll records. These can include information about people your company deals with — for example farmers’, agents’ and suppliers’ names, contact details, tax and bank details.</li>
<li><b>Files you upload</b> — photos and documents such as weighment slips, receipts, certificates and quality photos. Images are compressed, and may be read on our own servers to suggest form entries; no third party reads them.</li>
<li><b>Activity records</b> — an audit trail of changes made in the workspace (who changed what and when, with the IP address and device type), kept so your company can review and correct its records.</li>
<li><b>Location</b> — only when you choose to capture a pond or harvest location on a record. The app does not track your location in the background.</li>
<li><b>App and device</b> — if notifications are on, a push token for your phone, its platform (Android or iOS) and the app version, so messages reach the right device.</li>
</ul>

<h2>What stays on your phone</h2>
<p>If you turn on fingerprint or Face ID sign-in, your email and password are saved only in your phone’s secure storage (Android Keystore or iOS Keychain), locked behind your biometrics. Your fingerprint or face data never leaves the phone and is never seen by Aqua ERP. Files you download are saved to the app’s own storage on the phone. The camera is used only when you choose to take a photo or scan a barcode.</p>

<h2>How we use it</h2>
<ul>
<li>To provide the service your company has asked for: storing and showing its records, reports and documents.</li>
<li>To send the notifications, emails and messages your company has set up.</li>
<li>To keep the service secure, detect misuse, and recover from failures.</li>
</ul>
<p>We do not sell personal information or show advertising, and the app contains no advertising or analytics trackers. Optional features that learn from records (such as yield prediction) learn only from your own company’s records and only for your company.</p>

<h2>Who processes it for us</h2>
<ul>
<li><b>Google Cloud</b> (India and Asia-Pacific regions) — application hosting and file storage.</li>
<li><b>MongoDB Atlas</b> (Mumbai) — the database.</li>
<li><b>Cloudflare</b> — network security and delivery, and encrypted off-site backups.</li>
<li><b>Firebase Cloud Messaging</b> (Google) — delivering push notifications to the app.</li>
<li>Email and WhatsApp delivery providers — only for messages your company chooses to send.</li>
</ul>
<p>Each acts only on our instructions. Information may be disclosed where the law requires it.</p>

<h2>How long it is kept</h2>
<p>Records are kept while your company uses Aqua ERP, and for as long as your company needs them, for example for tax and export compliance. Backups are taken so the service can be restored after a failure; they are used only for recovery and are kept according to the backup retention set for your company. When a company stops using Aqua ERP, its workspace — and, at its request, its backups — are deleted.</p>

<h2>Security</h2>
<p>Connections are encrypted (HTTPS). Each company’s data is held in a database of its own. Access inside a company is limited by role, and changes are recorded in an audit trail.</p>

<h2>Your rights</h2>
<p>You can ask to see, correct or delete personal information about you, and to withdraw consent where it is the basis for processing, subject to records your company must keep by law. Ask your company’s administrator, or contact us at ${reach}. We respond within 30 days. You can raise a grievance at the same address, and you may complain to the Data Protection Board of India. To delete your account, see <a href="/account-deletion">Delete an account</a>.</p>

<h2>Children</h2>
<p>Aqua ERP is a workplace tool and is not intended for anyone under 18.</p>

<h2>Changes</h2>
<p>If this policy changes, the new version is posted here with a new effective date.</p>
`;

  const deletion = `
<p>Aqua ERP accounts are created by the company you work for, not by signing up in the app. You can have your account deleted in either of these ways:</p>
<ul>
<li><b>Ask your company’s administrator.</b> Administrators can deactivate or delete a user under Admin → Users. This takes effect immediately.</li>
<li><b>Ask us.</b> Email ${reach} from the address on your account, naming your company. We confirm with your company and delete the account within 30 days.</li>
</ul>

<h2>What is deleted</h2>
<p>Your sign-in, name, email address, phone number and the push-notification tokens for your phones. You will no longer be able to sign in. To also remove a fingerprint sign-in saved on your phone, uninstall the app or tap Change workspace on its sign-in screen.</p>

<h2>What is kept</h2>
<p>Business records you entered for your company — purchases, stock, invoices and the audit trail of who recorded them — belong to the company and are kept as long as it needs them, including for legal and tax requirements. Copies may remain in backups, which are used only to recover from a failure and are deleted according to your company’s backup retention.</p>
`;

  return {
    privacy: { slug: 'privacy', title: 'Privacy policy', html: privacy, entity: entity || 'Aqua ERP' },
    deletion: { slug: 'account-deletion', title: 'Delete an Aqua ERP account', html: deletion, entity: entity || 'Aqua ERP' },
  };
}

/** A complete, self-contained page (no JavaScript) for the static copy. */
export function standalonePage(doc) {
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
<title>${esc(doc.title)} — Aqua ERP</title>
<meta name="robots" content="index,follow">
<style>
:root{color-scheme:light}
body{margin:0;background:#f8fafc;color:#334155;font:15px/1.65 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif}
header{background:#fff;border-bottom:1px solid #e2e8f0}
.wrap{max-width:46rem;margin:0 auto;padding:1rem 1.25rem}
.brand{display:flex;align-items:center;gap:.5rem;font-weight:600;color:#0f172a;text-decoration:none}
.logo{width:2rem;height:2rem;border-radius:.5rem;background:linear-gradient(135deg,#2563eb,#06b6d4)}
h1{font-size:1.6rem;color:#0f172a;margin:1.5rem 0 .2rem}
h2{font-size:1.1rem;color:#0f172a;margin:1.6rem 0 .4rem}
.meta{color:#64748b;font-size:.85rem;margin:0 0 1.5rem}
a{color:#1d4ed8} ul{padding-left:1.25rem} li{margin:.35rem 0}
nav{margin-top:2.5rem;padding-top:1rem;border-top:1px solid #e2e8f0;display:flex;gap:1rem;font-size:.9rem}
</style></head>
<body>
<header><div class="wrap"><a class="brand" href="/"><span class="logo"></span>Aqua ERP</a></div></header>
<main class="wrap">
<h1>${esc(doc.title)}</h1>
<p class="meta">Effective ${EFFECTIVE} · ${esc(doc.entity)}</p>
${doc.html}
<nav><a href="/privacy">Privacy policy</a><a href="/account-deletion">Delete an account</a></nav>
</main>
</body></html>
`;
}
